import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import type { TeamMember } from '@/hooks/useProjectTeamMembers';

interface AssigneeBadgeProps {
  assignedUserId?: string | null;
  teamMembers: TeamMember[];
}

/**
 * Get initials from full name (e.g., "Nguyễn Văn A" → "NA")
 */
const getInitials = (fullName: string): string => {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Small avatar badge showing assignee in Plan tab
 * Displays avatar with tooltip showing full name
 */
export const AssigneeBadge = ({ assignedUserId, teamMembers }: AssigneeBadgeProps) => {
  if (!assignedUserId) return null;
  
  const assignee = teamMembers.find(m => m.id === assignedUserId);
  if (!assignee) return null;
  
  const initials = getInitials(assignee.full_name);
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Avatar className="w-5 h-5 shrink-0 cursor-default border-2 border-white shadow-[0_1px_3px_rgba(0,0,0,0.3)]">
            <AvatarImage 
              src={assignee.avatar_url || undefined} 
              alt={assignee.full_name}
              fallbackName={assignee.full_name}
            />
            <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {assignee.full_name}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
